import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Heart, Mail, Truck, Shield, MessageCircle, Share2, ChevronLeft, ChevronRight, MapPin, CreditCard, ExternalLink, ChevronDown, ChevronUp, Package, Award, Users, Clock, Video, ShoppingCart, DollarSign, Tag, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import ProductCard from '../components/ProductCard';
import { useSupabase } from '../contexts/SupabaseContext';
import { useProducts, Product } from '../hooks/useProducts';
import ChatButton from '../components/chat/ChatButton';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, loading, getProductById, getRelatedProducts } = useProducts();
  const { user, supabase } = useSupabase();
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('photos');
  const { addToCart, isInCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState('White');
  const [product, setProduct] = useState<Product | null>(null);
  const [productVariants, setProductVariants] = useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [showChatButton, setShowChatButton] = useState(false);
  const [supplierUserId, setSupplierUserId] = useState<string | null>(null);

  // State for quantity-based pricing display
  const [showQuantityPricing, setShowQuantityPricing] = useState(true);

  useEffect(() => {
    if (products.length > 0 && id) {
      const foundProduct = getProductById(id);
      setProduct(foundProduct || null);
      
      if (foundProduct) {
        setRelatedProducts(getRelatedProducts(foundProduct.id, 4));
        
        // Fetch product variants
        fetchProductVariants(foundProduct.id);
        
        // Fetch supplier user_id
        const fetchSupplierUserId = async () => {
          try {
            if (!supabase) {
              console.error('Supabase client is not available');
              return;
            }
            
            const { data, error } = await supabase
              .from('suppliers')
              .select('user_id')
              .eq('id', foundProduct.supplier_id)
              .single();
            
            if (!error && data) {
              setSupplierUserId(data.user_id);
            }
          } catch (err) {
            console.error('Error fetching supplier user_id:', err);
          }
        };
        
        fetchSupplierUserId();
      }
    }
  }, [products, id]);

  // Fetch product variants
  const fetchProductVariants = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .order('is_default', { ascending: false });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setProductVariants(data);
        // Set default variant
        const defaultVariant = data.find(v => v.is_default) || data[0];
        setSelectedVariant(defaultVariant);
        
        // Update selected attributes based on default variant
        if (defaultVariant.attributes) {
          const attributes = defaultVariant.attributes;
          if (attributes.size) setSelectedSize(attributes.size);
          if (attributes.color) setSelectedColor(attributes.color);
        }
      }
    } catch (err) {
      console.error('Error fetching product variants:', err);
    }
  };

  // Handle variant selection when attributes change
  useEffect(() => {
    if (productVariants.length > 0) {
      // Find variant that matches selected attributes
      const matchingVariant = productVariants.find(variant => {
        const attributes = variant.attributes || {};
        return attributes.size === selectedSize && attributes.color === selectedColor;
      });
      
      if (matchingVariant) {
        setSelectedVariant(matchingVariant);
      }
    }
  }, [selectedSize, selectedColor, productVariants]);

  // Mock product data for when real data is not available
  const mockProduct = {
    id: '1',
    name: 'Premium Bangladeshi Cotton Shirt',
    price: 29.99,
    quantity_prices: [
      { min_quantity: 1, price: 29.99 },
      { min_quantity: 50, price: 24.99 },
      { min_quantity: 100, price: 19.99 }
    ],
    originalPrice: 39.99,
    images: [
      'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'
    ],
    video: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    rating: 4.8,
    reviews: 245,
    description: 'Made from 100% premium Bangladeshi cotton, this shirt represents the finest in South Asian textile craftsmanship. Hand-picked cotton from the fertile plains of Bangladesh is woven using traditional techniques passed down through generations.',
    features: [
      '100% Premium Bangladeshi Cotton',
      'Pre-shrunk and colorfast',
      'Traditional weaving techniques',
      'Comfortable regular fit',
      'Machine washable'
    ],
    options: {
      sizes: [
        { value: 'S', label: 'Small', available: true },
        { value: 'M', label: 'Medium', available: true },
        { value: 'L', label: 'Large', available: true },
        { value: 'XL', label: 'X-Large', available: true },
        { value: 'XXL', label: 'XX-Large', available: false }
      ],
      colors: [
        { value: 'White', label: 'White', available: true, colorCode: '#FFFFFF' },
        { value: 'Blue', label: 'Navy Blue', available: true, colorCode: '#1E3A8A' },
        { value: 'Green', label: 'Forest Green', available: true, colorCode: '#166534' },
        { value: 'Black', label: 'Black', available: false, colorCode: '#000000' }
      ]
    },
    seller: {
      name: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh',
      rating: 4.9,
      totalProducts: 156,
      yearEstablished: 1995,
      verified: true,
      slug: 'bengal-textiles',
      description: 'Leading manufacturer of premium cotton garments with 25+ years of export experience.',
      specialties: ['Cotton T-Shirts', 'Polo Shirts', 'Casual Wear', 'Corporate Uniforms'],
      responseTime: '< 2 hours',
      responseRate: '98%'
    },
    shipping: {
      domestic: 'Free shipping within Bangladesh',
      international: '$5.99 worldwide shipping',
      estimatedDelivery: '7-14 business days'
    },
    specifications: {
      'Material': '100% Cotton',
      'Origin': 'Dhaka, Bangladesh',
      'Care Instructions': 'Machine wash cold',
      'Sizes Available': 'S, M, L, XL, XXL',
      'Colors': 'White, Blue, Green'
    }
  };

  // Use mock data if real product is not available
  const displayProduct = product || mockProduct;

  // Related products data
  const mockRelatedProducts = [
    {
      id: '2',
      name: 'Handwoven Jamdani Saree',
      price: 89.99,
      image: 'https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg',
      rating: 4.9,
      reviews: 167,
      seller: 'Heritage Weavers',
      location: 'Dhaka, Bangladesh',
      badge: 'Authentic'
    },
    {
      id: '3',
      name: 'Traditional Bengali Kurta',
      price: 34.99,
      originalPrice: 49.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.7,
      reviews: 198,
      seller: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh'
    },
    {
      id: '4',
      name: 'Premium Cotton Polo Shirt',
      price: 24.99,
      originalPrice: 32.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.6,
      reviews: 134,
      seller: 'Cotton Craft BD',
      location: 'Chittagong, Bangladesh',
      badge: 'Popular'
    },
    {
      id: '5',
      name: 'Organic Cotton T-Shirt',
      price: 19.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.5,
      reviews: 89,
      seller: 'Eco Textiles',
      location: 'Sylhet, Bangladesh'
    }
  ];

  // Supplier's popular products
  const supplierPopularProducts = [
    {
      id: '6',
      name: 'Cotton Polo Shirt - Corporate',
      price: 24.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.8,
      reviews: 189,
      seller: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh'
    },
    {
      id: '7',
      name: 'Premium Cotton Hoodie',
      price: 45.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.7,
      reviews: 156,
      seller: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh'
    },
    {
      id: '8',
      name: 'Cotton Casual Pants',
      price: 39.99,
      image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      rating: 4.6,
      reviews: 134,
      seller: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh'
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "What sizes are available for this shirt?",
      answer: "This premium cotton shirt is available in sizes S, M, L, XL, and XXL. Please refer to our size chart for detailed measurements. If you need assistance with sizing, feel free to contact the seller directly."
    },
    {
      question: "How do I care for this cotton shirt?",
      answer: "This shirt is machine washable in cold water. We recommend washing with similar colors and avoiding bleach. Tumble dry on low heat or hang dry to maintain the fabric's quality. Iron on medium heat if needed."
    },
    {
      question: "Is this shirt pre-shrunk?",
      answer: "Yes, this shirt is pre-shrunk during the manufacturing process to ensure it maintains its size and fit after washing. You can expect minimal to no shrinkage with proper care."
    },
    {
      question: "What is the return policy for this product?",
      answer: "We offer a 30-day return policy for unworn items in original condition with tags attached. Return shipping costs may apply for international orders. Please contact the seller for specific return instructions."
    },
    {
      question: "How long does shipping take?",
      answer: "Domestic shipping within Bangladesh is free and typically takes 3-5 business days. International shipping costs $5.99 and takes 7-14 business days depending on your location. Tracking information will be provided once shipped."
    },
    {
      question: "Can I get this shirt in custom colors?",
      answer: "Currently, this shirt is available in White, Blue, and Green. For custom color requests or bulk orders, please contact Bengal Textiles Ltd. directly through the 'Contact Seller' button."
    }
  ];

  const discount = displayProduct.originalPrice ? Math.round(((displayProduct.originalPrice - displayProduct.price) / displayProduct.originalPrice) * 100) : 0;

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleVisitStore = () => {
    alert(`Visit seller store: https://${displayProduct.seller.slug}.madeinbd.com`);
  };

  const handleContactSeller = () => {
    setShowChatButton(true);
  };
  
  // Handle Start Order button click
  const handleStartOrder = () => {
    // Create order data to pass to checkout page
    const variantPrice = selectedVariant ? selectedVariant.price : (product?.price || displayProduct.price);
    const variantOriginalPrice = selectedVariant ? selectedVariant.original_price : (product?.original_price || displayProduct.originalPrice);
    
    const cartItem = {
      id: Date.now().toString(),
      productId: product?.id || displayProduct.id,
      name: product?.name || displayProduct.name,
      price: variantPrice,
      quantity: quantity,
      image: product?.image_url || displayProduct.images[0],
      size: selectedSize,
      color: selectedColor,
      originalPrice: variantOriginalPrice,
      variantId: selectedVariant?.id,
      supplier: {
        id: product?.supplier_id || '1',
        name: product?.supplier_name || displayProduct.seller.name
      }
    };
    
    // Add to cart and navigate to cart page
    addToCart(cartItem);
    navigate('/cart');
  };
  
  // Handle Send Inquiry button click
  const handleSendInquiry = () => {
    // Set up inquiry data
    const inquiryTopic = `Inquiry about ${product?.name || displayProduct.name}`;
    
    // Show chat button with supplier and pre-filled topic
    setShowChatButton(true);
  };
  
  // Handle Buy For Me button click
  const handleBuyForMe = () => {
    // Navigate to contact page with buy-for-me service parameter
    navigate('/contact?service=buy-for-me&product=' + encodeURIComponent(product?.name || displayProduct.name));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Image Gallery - Left Sidebar */}
          <div className="lg:col-span-5 space-y-4">
            {/* Tabs for Photos/Video */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveTab('photos')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'photos'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Photos
              </button>
              <button
                onClick={() => setActiveTab('video')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'video'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Video
              </button>
            </div>
            
            {/* Main Image or Video */}
            <div className="relative">
              {activeTab === 'photos' ? (
                <img 
                  src={product?.image_url || displayProduct.images[selectedImage]} 
                  alt={displayProduct.name}
                  className="w-full h-96 lg:h-[500px] object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-96 lg:h-[500px] rounded-lg overflow-hidden">
                  <iframe
                    src={displayProduct.video}
                    title="Product Video"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              <button className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-50">
                <Heart className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            {/* Thumbnails */}
            {activeTab === 'photos' && (
              <div className="flex space-x-2">
                {(product?.image_url ? [product.image_url] : displayProduct.images).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      selectedImage === index ? 'border-orange-600' : 'border-gray-200'
                    }`}
                  >
                    <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
                {displayProduct.video && (
                  <button
                    onClick={() => setActiveTab('video')}
                    className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 relative"
                  >
                    <img src={product?.image_url || displayProduct.images[0]} alt="Video thumbnail" className="w-full h-full object-cover opacity-70" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Video className="h-8 w-8 text-white" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{displayProduct.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`h-5 w-5 ${i < Math.floor(displayProduct.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                    />
                  ))}
                  <span className="text-lg font-medium ml-2">{displayProduct.rating}</span>
                </div>
                <span className="text-gray-600">({displayProduct.reviews} reviews)</span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center space-x-4">
              <span className="text-3xl font-bold text-orange-600">${displayProduct.price}</span>
              {displayProduct.originalPrice && (
                <span className="text-xl text-gray-500 line-through">${displayProduct.originalPrice}</span>
              )}
              {displayProduct.originalPrice && (
                <span className="bg-red-100 text-red-600 px-2 py-1 text-sm font-semibold rounded">
                  -{discount}% OFF
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <p className="text-gray-700 leading-relaxed">{product?.description || displayProduct.description}</p>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Key Features</h3>
              <ul className="space-y-2">
                {(product?.features || displayProduct.features).map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Product Options */}
            <div className="space-y-6">
              {/* Size Options */}
              <div>
                <h3 className="text-lg font-medium mb-3">Size</h3>
                <div className="flex flex-wrap gap-3">
                  {(productVariants.length > 0 
                    ? [...new Set(productVariants.map(v => v.attributes?.size || ''))]
                        .filter(Boolean)
                        .map(size => ({ 
                          value: size, 
                          label: size, 
                          available: true 
                        }))
                    : displayProduct.options?.sizes || []
                  ).map((size) => (
                    <label
                      key={size.value}
                      className={`relative flex items-center cursor-pointer ${
                        !size.available ? 'cursor-not-allowed opacity-50' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="size"
                        value={size.value}
                        checked={selectedSize === size.value}
                        onChange={(e) => setSelectedSize(e.target.value)}
                        disabled={!size.available}
                        className="sr-only"
                      />
                      <div className={`px-4 py-2 border-2 rounded-lg text-sm font-medium transition-all ${
                        selectedSize === size.value
                          ? 'border-orange-600 bg-orange-50 text-orange-700'
                          : size.available
                          ? 'border-gray-300 hover:border-orange-300 text-gray-700'
                          : 'border-gray-200 text-gray-400'
                      }`}>
                        {size.label}
                        {!size.available && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-full h-0.5 bg-gray-400 transform rotate-45"></div>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Color Options */}
              <div>
                <h3 className="text-lg font-medium mb-3">Color</h3>
                <div className="flex flex-wrap gap-3">
                  {(productVariants.length > 0
                    ? [...new Set(productVariants.map(v => v.attributes?.color || ''))]
                        .filter(Boolean)
                        .map(color => {
                          const displayColor = displayProduct.options?.colors?.find(c => c.value === color);
                          return {
                            value: color,
                            label: color,
                            available: true,
                            colorCode: displayColor?.colorCode || '#000000'
                          };
                        })
                    : displayProduct.options?.colors || []
                  ).map((color) => (
                    <label
                      key={color.value}
                      className={`relative flex items-center cursor-pointer ${
                        !color.available ? 'cursor-not-allowed opacity-50' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="color"
                        value={color.value}
                        checked={selectedColor === color.value}
                        onChange={(e) => setSelectedColor(e.target.value)}
                        disabled={!color.available}
                        className="sr-only"
                      />
                      <div className={`flex items-center space-x-3 px-4 py-2 border-2 rounded-lg text-sm font-medium transition-all ${
                        selectedColor === color.value
                          ? 'border-orange-600 bg-orange-50 text-orange-700'
                          : color.available
                          ? 'border-gray-300 hover:border-orange-300 text-gray-700'
                          : 'border-gray-200 text-gray-400'
                      }`}>
                        <div 
                          className={`w-4 h-4 rounded-full border ${
                            color.colorCode === '#FFFFFF' ? 'border-gray-300' : 'border-gray-200'
                          }`}
                          style={{ backgroundColor: color.colorCode }}
                        ></div>
                        <span>{color.label}</span>
                        {!color.available && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-full h-0.5 bg-gray-400 transform rotate-45"></div>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Quantity-Based Pricing */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium">Quantity-Based Pricing</h3>
                <button
                  onClick={() => setShowQuantityPricing(!showQuantityPricing)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {showQuantityPricing ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
              </div>
              
              {showQuantityPricing && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity Range
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price Per Unit
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Discount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(product?.quantity_prices || displayProduct.quantity_prices).map((tier, index, array) => {
                          const nextTier = index < array.length - 1 ? array[index + 1].min_quantity - 1 : null;
                          const basePrice = selectedVariant ? selectedVariant.price : array[0].price;
                          const discountPercent = ((basePrice - tier.price) / basePrice * 100).toFixed(0);
                          
                          return (
                            <tr key={index} className={index === 0 ? "" : "bg-green-50"}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {nextTier 
                                  ? `${tier.min_quantity} - ${nextTier}`
                                  : `${tier.min_quantity}+`
                                }
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                                  <span className="font-semibold">{tier.price.toFixed(2)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {index === 0 ? (
                                  <span className="text-gray-500">-</span>
                                ) : (
                                  <span className="text-green-600 font-medium flex items-center">
                                    <Tag className="h-4 w-4 mr-1" />
                                    Save {discountPercent}%
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    * Bulk pricing applied automatically at checkout based on quantity ordered
                  </p>
                </div>
              )}
            </div>

            {/* Quantity and Buttons */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="text-lg font-medium">Quantity:</label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 hover:bg-gray-100"
                  >
                    -
                  </button>
                  <span className="px-4 py-2 border-l border-r border-gray-300">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Send Inquiry - Primary Button */}
                <div className="flex space-x-3">
                  <button 
                    onClick={handleStartOrder}
                    className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 hover:shadow-md active:scale-95"
                  >
                    <ShoppingBag className="h-4 w-4" />
                    <span>{isInCart(product?.id || displayProduct.id) ? 'Add More to Cart' : 'Add to Cart'}</span>
                    {selectedVariant && <span className="ml-1 text-xs">({selectedSize}/{selectedColor})</span>}
                  </button>
                  <button 
                    onClick={handleSendInquiry}
                    className="flex-1 bg-orange-600 text-white py-2.5 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2 hover:shadow-md active:scale-95"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Send Inquiry</span>
                  </button>
                </div>

                {/* Buy For Me and Contact Seller */}
                <div className="flex space-x-3">
                  <button 
                    onClick={handleBuyForMe}
                    className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 hover:shadow-md active:scale-95"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span>Buy For Me</span>
                  </button>
                  <button 
                    onClick={handleContactSeller}
                    className="flex-1 border border-orange-600 text-orange-600 py-2.5 rounded-lg font-medium hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2 hover:shadow-md active:scale-95"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>Chat with Seller</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Truck className="h-5 w-5 text-orange-600" />
                <span className="font-medium">Shipping Information</span>
              </div>
              <div className="space-y-1 text-gray-700">
                <p>• {displayProduct.shipping.domestic}</p>
                <p>• International: {displayProduct.shipping.international}</p>
                <p>• Estimated delivery: {displayProduct.shipping.estimatedDelivery}</p>
                {selectedVariant && selectedVariant.stock_quantity <= 10 && (
                  <p className="text-orange-600 font-medium mt-2">• Only {selectedVariant.stock_quantity} left in stock!</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Know Your Supplier Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-8">Know Your Supplier</h2>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Supplier Info */}
              <div className="lg:col-span-2">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-gradient-to-br from-orange-100 to-red-100 w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Package className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{displayProduct.seller.name}</h3>
                      {displayProduct.seller.verified && (
                        <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                          <Shield className="h-4 w-4" />
                          <span>Verified</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="font-medium">{displayProduct.seller.rating}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span>{displayProduct.seller.location}</span>
                      </div>
                      <span className="text-gray-600">Est. {displayProduct.seller.yearEstablished}</span>
                    </div>
                    <p className="text-gray-700 mb-4">{displayProduct.seller.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {displayProduct.seller.specialties.map((specialty, index) => (
                        <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Supplier Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 mb-1">{displayProduct.seller.totalProducts}</div>
                    <div className="text-sm text-gray-600">Products</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 mb-1">{displayProduct.seller.responseRate}</div>
                    <div className="text-sm text-gray-600">Response Rate</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 mb-1">{displayProduct.seller.responseTime}</div>
                    <div className="text-sm text-gray-600">Response Time</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 mb-1">{new Date().getFullYear() - displayProduct.seller.yearEstablished}+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                </div>
              </div>

              {/* Contact Actions */}
              <div className="space-y-4">
                <button 
                  onClick={handleVisitStore}
                  className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Visit Store</span>
                </button>
                <button 
                  onClick={handleContactSeller}
                  className="w-full border border-orange-600 text-orange-600 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Chat with Supplier</span>
                </button>
                
                {/* Trust Indicators */}
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Award className="h-4 w-4 text-green-600" />
                    <span>Quality Certified</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span>Trusted by 500+ Buyers</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4 text-purple-600" />
                    <span>Fast Response Guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Supplier's Popular Products */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">More from {displayProduct.seller.name}</h2>
            <button 
              onClick={handleVisitStore}
              className="text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-1"
            >
              <span>View All Products</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supplierPopularProducts.map((supplierProduct) => (
              <ProductCard key={supplierProduct.id} product={supplierProduct} />
            ))}
          </div>
        </div>

        {/* Product Specifications */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Specifications</h2>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(displayProduct.specifications).map(([key, value]) => (
                <div key={key} className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                  <span className="font-medium text-gray-700">{key}:</span>
                  <span className="text-gray-600">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Frequently Asked Questions */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Ask a Question */}
          <div className="mt-8 bg-orange-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Have a question not listed here?</h3>
            <p className="text-gray-700 mb-4">
              Feel free to contact the seller directly for any specific questions about this product.
            </p>
            <button 
              onClick={handleContactSeller}
              className="bg-orange-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
            >
              <MessageCircle className="h-4 w-4" />
              <span>Chat with Seller</span>
            </button>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Related Products</h2>
            <Link to="/products" className="text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-1">
              <span>View All</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(relatedProducts.length > 0 ? relatedProducts : mockRelatedProducts).map((relatedProduct) => (
              <ProductCard key={relatedProduct.id} product={relatedProduct} />
            ))}
          </div>
        </div>
      </div>

      {/* Chat Button with Supplier */}
      {showChatButton && supplierUserId && (
        <ChatButton initialUserId={supplierUserId} initialTopic={`Inquiry about ${displayProduct.name}`} />
      )}
    </div>
  );
};

export default ProductDetailPage;